"use client";

import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

export type InfluenceCreatorCardProps = {
  avatar: string;
  name: string;
  role?: string;
  type: "output" | "input";
  source?: string;
  sourceUrl?: string;
  episode?: string;
  onViewInfluence?: () => void;
};

export default function InfluenceCreatorCard({
  avatar,
  name,
  role,
  type,
  source,
  sourceUrl,
  episode,
  onViewInfluence,
}: InfluenceCreatorCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const hoverDirection = type === "output" ? "hover:-translate-x-1" : "hover:translate-x-1";
  const hoverBorderColor =
    type === "output" ? "hover:border-[#ff6b6b]/50" : "hover:border-[#667eea]/50";

  return (
    <>
      <div
        onClick={() => setIsOpen(true)}
        className={`flex w-32 shrink-0 cursor-pointer flex-col items-center gap-3 rounded-2xl border border-[#a78bfa]/30 bg-gradient-to-br from-[#667eea]/10 to-[#764ba2]/10 p-4 backdrop-blur-md transition-all duration-300 hover:bg-white/10 lg:w-auto lg:shrink lg:flex-row lg:items-center lg:gap-4 lg:p-5 ${hoverDirection} ${hoverBorderColor}`}
      >
        {/* 丸型アバター */}
        <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] text-2xl shadow-[0_4px_12px_rgba(102,126,234,0.3)] lg:size-20">
          {avatar}
        </div>
        <div className="text-center lg:flex-1 lg:text-left">
          {/* Creatorラベル */}
          <div className="mb-1 inline-block rounded-full bg-[#667eea]/20 px-2 py-0.5 text-[10px] font-medium text-[#a78bfa] lg:text-xs">
            Creator
          </div>
          <div className="mb-1 text-sm font-semibold">{name}</div>
          {role && (
            <div className="mt-1 text-xs uppercase tracking-wider text-[#667eea] lg:text-sm">
              {role}
            </div>
          )}
        </div>
      </div>

      {/* 作家詳細モーダル */}
      <Dialog
        open={isOpen}
        onClose={setIsOpen}
        className="relative z-10"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto p-4">
          <DialogPanel
            transition
            className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#1a1a2e] p-6 shadow-2xl transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          >
            {/* クローズボタン */}
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute right-4 top-4 text-white/50 hover:text-white"
            >
              <XMarkIcon className="size-6" />
            </button>

            {/* モーダル内容 */}
            <div className="flex flex-col items-center">
              {/* アバター */}
              <div className="flex size-24 items-center justify-center rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] text-4xl shadow-[0_10px_30px_rgba(102,126,234,0.5)]">
                {avatar}
              </div>

              {/* 名前 */}
              <h3 className="mt-3 text-center text-lg font-bold text-white">{name}</h3>

              {/* 役職 */}
              {role && (
                <p className="mt-2 text-sm uppercase tracking-widest text-[#667eea]">{role}</p>
              )}

              {/* エピソード */}
              {episode && (
                <div className="mt-4 w-full">
                  <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-widest text-[#667eea]">episode</p>
                  <p className="text-center text-sm leading-relaxed text-[#c4c9d8]">{episode}</p>
                </div>
              )}

              {/* ソース情報 */}
              {(source || sourceUrl) && (
                <div className="mt-4 w-full">
                  <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-widest text-[#667eea]">source</p>
                  {sourceUrl ? (
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-center text-sm text-[#4ecdc4] underline underline-offset-2 transition-opacity hover:opacity-70"
                    >
                      {source ?? "source"}
                    </a>
                  ) : (
                    <span className="block text-center text-sm text-[#c4c9d8]">{source}</span>
                  )}
                </div>
              )}

              {/* View Influence ボタン */}
              {onViewInfluence && (
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    onViewInfluence();
                  }}
                  className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-white/20 px-5 py-2 text-sm font-medium text-white/70 transition-colors duration-200 hover:border-white/40 hover:text-white"
                >
                  View Influence →
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
